import { BlockType } from '../lib/types';

/** VSM Lucidchart-aligned symbol id (Famla Canvas — PRD DMN/VSM/RACI). */
export type VsmSymbolId =
  | 'customerSupplier'
  | 'dedicatedProcessFlow'
  | 'sharedProcess'
  | 'dataBox'
  | 'workcell'
  | 'inventory'
  | 'shipments'
  | 'pushArrow'
  | 'supermarket'
  | 'materialPull'
  | 'fifoLane'
  | 'safetyStock'
  | 'externalShipment'
  | 'productionControl'
  | 'manualInfo'
  | 'electronicInfo'
  | 'productionKanban'
  | 'withdrawalKanban'
  | 'signalKanban'
  | 'kanbanPost'
  | 'sequencedPull'
  | 'loadLevelling'
  | 'mrpErp'
  | 'goSee'
  | 'verbalInformation'
  | 'kaizenBurst'
  | 'operator'
  | 'otherInformation'
  | 'timeline'
  | 'transportationRail'
  | 'transportationAir'
  | 'transportationBoat'
  | 'forklift'
  | 'expedited'
  | 'milkRun'
  | 'warehouse'
  | 'crossDock'
  | 'orders'
  | 'phone'
  | 'batchedKanban'
  | 'controlCenter'
  | 'qualityProblem'
  | 'solutionImprovement';

export type VsmCategoryId = 'process' | 'material' | 'information' | 'general';

/** How the symbol behaves in the editor (PRD §5). */
export type VsmSymbolKind = 'node' | 'connector' | 'marker';

export interface VsmSymbolDefinition {
  id: VsmSymbolId;
  label: string;
  category: VsmCategoryId;
  kind: VsmSymbolKind;
  /** Maps to swimlane block type for drop / layers until dedicated VSM canvas exists. */
  blockType: BlockType;
  defaultText: string;
  colorToken: 'neutral' | 'orange' | 'blue';
  /** True when the palette item drops a node/marker onto the canvas. Connectors are line tools. */
  draggable: boolean;
}

export interface VsmCategoryDefinition {
  id: VsmCategoryId;
  label: string;
  /** Most-used symbols shown in Row 1 (PRD §7.2). */
  row1: VsmSymbolId[];
  symbols: VsmSymbolDefinition[];
}

const kindBlockType = (kind: VsmSymbolKind): BlockType =>
  kind === 'marker' ? 'note' : 'process';

const kindColor = (kind: VsmSymbolKind): VsmSymbolDefinition['colorToken'] => {
  if (kind === 'connector') return 'blue';
  if (kind === 'marker') return 'orange';
  return 'neutral';
};

const symbol = (
  id: VsmSymbolId,
  label: string,
  category: VsmCategoryId,
  kind: VsmSymbolKind
): VsmSymbolDefinition => ({
  id,
  label,
  category,
  kind,
  blockType: kindBlockType(kind),
  defaultText: label,
  colorToken: kindColor(kind),
  draggable: kind !== 'connector',
});

export const VSM_SYMBOLS: Record<VsmSymbolId, VsmSymbolDefinition> = {
  customerSupplier: symbol('customerSupplier', 'Customer / Supplier', 'process', 'node'),
  dedicatedProcessFlow: symbol('dedicatedProcessFlow', 'Dedicated Process Flow', 'process', 'node'),
  sharedProcess: symbol('sharedProcess', 'Shared Process', 'process', 'node'),
  dataBox: symbol('dataBox', 'Data Box', 'process', 'marker'),
  workcell: symbol('workcell', 'Workcell', 'process', 'node'),
  inventory: symbol('inventory', 'Inventory', 'material', 'node'),
  shipments: symbol('shipments', 'Shipments', 'material', 'connector'),
  pushArrow: symbol('pushArrow', 'Push Arrow', 'material', 'connector'),
  supermarket: symbol('supermarket', 'Supermarket', 'material', 'node'),
  materialPull: symbol('materialPull', 'Material Pull', 'material', 'connector'),
  fifoLane: symbol('fifoLane', 'FIFO Lane', 'material', 'connector'),
  safetyStock: symbol('safetyStock', 'Safety Stock', 'material', 'node'),
  externalShipment: symbol('externalShipment', 'External Shipment', 'material', 'marker'),
  productionControl: symbol('productionControl', 'Production Control', 'information', 'node'),
  manualInfo: symbol('manualInfo', 'Manual Info', 'information', 'connector'),
  electronicInfo: symbol('electronicInfo', 'Electronic Info', 'information', 'connector'),
  productionKanban: symbol('productionKanban', 'Production Kanban', 'information', 'marker'),
  withdrawalKanban: symbol('withdrawalKanban', 'Withdrawal Kanban', 'information', 'marker'),
  signalKanban: symbol('signalKanban', 'Signal Kanban', 'information', 'marker'),
  kanbanPost: symbol('kanbanPost', 'Kanban Post', 'information', 'node'),
  sequencedPull: symbol('sequencedPull', 'Sequenced Pull', 'information', 'connector'),
  loadLevelling: symbol('loadLevelling', 'Load Levelling', 'information', 'node'),
  mrpErp: symbol('mrpErp', 'MRP / ERP', 'information', 'node'),
  goSee: symbol('goSee', 'Go See', 'information', 'marker'),
  verbalInformation: symbol('verbalInformation', 'Verbal Information', 'information', 'connector'),
  kaizenBurst: symbol('kaizenBurst', 'Kaizen Burst', 'general', 'marker'),
  operator: symbol('operator', 'Operator', 'general', 'marker'),
  otherInformation: symbol('otherInformation', 'Other Information', 'general', 'node'),
  timeline: symbol('timeline', 'Timeline', 'general', 'node'),
  transportationRail: symbol('transportationRail', 'Transportation — Rail', 'general', 'marker'),
  transportationAir: symbol('transportationAir', 'Transportation — Air', 'general', 'marker'),
  transportationBoat: symbol('transportationBoat', 'Transportation — Boat', 'general', 'marker'),
  forklift: symbol('forklift', 'Forklift', 'general', 'marker'),
  expedited: symbol('expedited', 'Expedited', 'general', 'marker'),
  milkRun: symbol('milkRun', 'Milk Run', 'general', 'marker'),
  warehouse: symbol('warehouse', 'Warehouse', 'general', 'node'),
  crossDock: symbol('crossDock', 'Cross-Dock', 'general', 'node'),
  orders: symbol('orders', 'Orders', 'general', 'marker'),
  phone: symbol('phone', 'Phone', 'general', 'marker'),
  batchedKanban: symbol('batchedKanban', 'Batched Kanban', 'general', 'marker'),
  controlCenter: symbol('controlCenter', 'Control Center', 'general', 'node'),
  qualityProblem: symbol('qualityProblem', 'Quality Problem', 'general', 'marker'),
  solutionImprovement: symbol('solutionImprovement', 'Solution / Improvement', 'general', 'marker'),
};

const ROW1: Record<VsmCategoryId, VsmSymbolId[]> = {
  process: ['customerSupplier', 'dedicatedProcessFlow', 'dataBox'],
  material: ['inventory', 'supermarket', 'safetyStock', 'externalShipment'],
  information: ['productionControl', 'productionKanban', 'kanbanPost', 'loadLevelling'],
  general: ['kaizenBurst', 'operator', 'timeline', 'qualityProblem'],
};

const CATEGORY_LABELS: Record<VsmCategoryId, string> = {
  process: 'Process',
  material: 'Material',
  information: 'Information',
  general: 'General',
};

const CATEGORY_ORDER: VsmCategoryId[] = ['process', 'material', 'information', 'general'];

export const VSM_CATEGORIES: VsmCategoryDefinition[] = CATEGORY_ORDER.map((id) => {
  // Connectors / line tools are registered but deferred from the palette for now.
  const symbols = Object.values(VSM_SYMBOLS).filter((s) => s.category === id && s.kind !== 'connector');
  return {
    id,
    label: CATEGORY_LABELS[id],
    row1: ROW1[id].filter((sid) => VSM_SYMBOLS[sid]?.kind !== 'connector'),
    symbols,
  };
});

export function getVsmSymbol(id: VsmSymbolId | string | undefined): VsmSymbolDefinition | undefined {
  if (!id) return undefined;
  return VSM_SYMBOLS[id as VsmSymbolId];
}

export function isVsmSymbolId(value: string): value is VsmSymbolId {
  return value in VSM_SYMBOLS;
}

export function getVsmDropDimensions(id: VsmSymbolId): { width: number; height: number } {
  const def = VSM_SYMBOLS[id];
  if (!def) return { width: 140, height: 80 };
  if (def.kind === 'marker') return { width: 72, height: 64 };
  if (id === 'timeline') return { width: 360, height: 72 };
  if (id === 'customerSupplier') return { width: 120, height: 100 };
  if (id === 'dataBox') return { width: 160, height: 100 };
  return { width: 140, height: 80 };
}
